import React from "react";
import {
  FaFacebook,
  FaInstagram,
  FaLinkedin,
  FaTelegram,
  FaWhatsapp,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import smartLogo from "../../assets/images/smart-logo.png";
import { Button, Container } from "./ui";
import { useSectionNav } from "./ui/useSectionNav";

const socialLinks = [
  {
    icon: FaWhatsapp,
    href: "https://wa.me/+919363282829",
    label: "WhatsApp",
    color: "hover:bg-green-500",
  },
  {
    icon: FaFacebook,
    href: "https://www.facebook.com/profile.php?id=61590478562048",
    label: "Facebook",
    color: "hover:bg-blue-600",
  },
  {
    icon: FaInstagram,
    href: "https://www.instagram.com/smartica.in/",
    label: "Instagram",
    color: "hover:bg-pink-500",
  },
  {
    icon: FaXTwitter,
    href: "https://x.com/ExcelwithSMART",
    label: "Twitter",
    color: "hover:bg-black",
  },
  {
    icon: FaLinkedin,
    href: "https://www.linkedin.com/company/101747817/admin/dashboard/",
    label: "LinkedIn",
    color: "hover:bg-blue-700",
  },
  {
    icon: FaTelegram,
    href: "https://t.me/excelwithsmarttraining",
    label: "Telegram",
    color: "hover:bg-blue-500",
  },
];

const companyLinks = [
  { title: "Terms & Conditions", href: "/policies/TermsAndConditions" },
  { title: "Cancellation & Refund Policy", href: "/policies/CancellationPolicy" },
  { title: "Privacy Policy", href: "/policies/PrivacyPolicy" },
];

const officeAddress =
  "Flat No.3, Ground Floor, Raghupriya Apartment, Krishnamma road, Nungambakkam, Chennai - 600034";
const mapsDirectionsUrl = "https://maps.app.goo.gl/2AAJHT8Jg4veerqA7";
const mapsEmbedUrl =
  "https://www.google.com/maps?q=13.0595984,80.2461704&z=17&output=embed";

const contactInfo = [
  { icon: FaMapMarkerAlt, content: officeAddress },
  { icon: FaPhone, content: "+91 9500077606", href: "tel:+919500077606" },
  { icon: FaPhone, content: "+91 9363282829", href: "tel:+919363282829" },
  {
    icon: FaEnvelope,
    content: "info@smartica.co.in",
    href: "mailto:info@smartica.co.in",
  },
];

const Footer = ({ setActiveKey }) => {
  const goToSection = useSectionNav();

  // setActiveKey only exists on the home page; off-route the nav falls back to
  // routing home, where the default category is already selected.
  const showService = (key) => {
    setActiveKey?.(key);
    goToSection("services");
  };

  const quickLinks = [
    { title: "About Us", onClick: () => goToSection("about") },
    { title: "Colleges", onClick: () => showService(1) },
    { title: "Corporates", onClick: () => showService(2) },
    { title: "Schools", onClick: () => showService(3) },
  ];

  const linkClass =
    "text-left text-sm text-white/70 transition-colors duration-300 hover:text-white";

  return (
    <footer className="bg-[#1F2937] text-white">
      <Container className="py-16">
        <div className="grid gap-10 lg:grid-cols-4">
          {/* Brand */}
          <div>
            <img src={smartLogo} alt="SMART Logo" className="mb-5 h-14 w-auto" />
            <p className="text-sm leading-relaxed text-white">
              A destination for industry relevant and result oriented 'Skill
              Development and Placement Training Programmes.' Empowering students
              with the skills they need to succeed in today's competitive world.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                    className={`w-10 h-10 bg-gray-700 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110 ${social.color} group`}                >
                  <social.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="mb-5 text-sm font-bold uppercase tracking-wider text-white">
              Quick Links
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.title}>
                  <button onClick={link.onClick} className={linkClass}>
                    {link.title}
                  </button>
                </li>
              ))}
            </ul>

            <h3 className="mb-5 mt-8 text-sm font-bold uppercase tracking-wider text-white">
              Company
            </h3>
            <ul className="space-y-3">
              {companyLinks.map((link) => (
                <li key={link.title}>
                  <a href={link.href} className={linkClass}>
                    {link.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="mb-5 text-sm font-bold uppercase tracking-wider text-white">
              Contact Us
            </h3>
            <ul className="space-y-4">
              {contactInfo.map((contact, index) => (
                <li key={index} className="flex items-start gap-3">
                  <contact.icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-white" />
                  {contact.href ? (
                    <a href={contact.href} className={linkClass}>
                      {contact.content}
                    </a>
                  ) : (
                    <span className="text-sm leading-relaxed text-white/70">
                      {contact.content}
                    </span>
                  )}
                </li>
              ))}
            </ul>

            <Button
              as="a"
              href={mapsDirectionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="sm"
              className="mt-6"
            >
              <FaMapMarkerAlt className="h-3.5 w-3.5" />
              Get Directions
            </Button>
          </div>

          {/* Map */}
          <div>
            <h3 className="mb-5 text-sm font-bold uppercase tracking-wider text-white">
              Find Us
            </h3>
            <div className="overflow-hidden rounded-2xl border border-white/10">
              <iframe
                title="SMART Training Resources — Office Location"
                src={mapsEmbedUrl}
                width="100%"
                height="220"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </Container>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <Container className="py-5">
          <p className="text-center text-xs text-white/50">
            Copyrights © {new Date().getFullYear()} SMARTICA | SMART TRAINING
            RESOURCES
          </p>
        </Container>
      </div>
    </footer>
  );
};

export default Footer;
